import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Switch from "../../components/ui/Switch";
import Textarea from "../../components/ui/Textarea";
import Button from "../../components/ui/Button";
import { Edit2, Mail } from "lucide-react";
import {
    getUsersRequest,
    updateUserRequest,
    downloadAgentsExportRequest,
    sendDistributorMessageRequest,
    UserItem,
} from "../../services/api/users.api";
import { registerCustomerRequest } from "../../services/api/auth.api";
import { triggerBrowserDownload } from "../../services/api/orders.api";
import { getCitiesRequest } from "../../services/api/geo.api";
import { CityItem } from "../../services/types/geo.types";

const PAGE_SIZE = 20;

const DEFAULT_DISTRIBUTOR_MESSAGE_SUBJECT = "Аккаунт распространителя ShowMust";

const buildDefaultDistributorMessageBody = (email: string): string =>
    [
        "Добрый день.",
        "Высылаем вам доступы и краткую инструкцию с описанием нашего сервиса.",
        "Порядок оформления заказа и применение скидок описаны в документе.",
        "",
        "https://showmust.de/",
        `Логин: ${email}`,
        "Пароль: ",
        "",
        "С уважением,",
        "ShowMust",
    ].join("\n");

const DEFAULT_DISTRIBUTOR_ATTACHMENT_HINT = "instruction.pdf (по умолчанию, лежит на сервере)";

const formatDateTime = (value: string | null): string => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const UsersPage: React.FC = () => {
    const [items, setItems] = useState<UserItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(0);

    const [isLoading, setIsLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isActionSubmitting, setIsActionSubmitting] = useState(false);

    // фильтры
    const [isAgentFilter, setIsAgentFilter] = useState<boolean | null>(null);
    const [clubMemberFilter, setClubMemberFilter] = useState<boolean | null>(null);
    const [isActiveFilter, setIsActiveFilter] = useState<boolean | null>(null);
    const [firstNameFilter, setFirstNameFilter] = useState("");
    const [emailFilter, setEmailFilter] = useState("");
    const [discountModalUser, setDiscountModalUser] = useState<UserItem | null>(null);
    const [discountModalValue, setDiscountModalValue] = useState<string>("");

    // Модалка редактирования пользователя (всё, кроме email и пароля)
    const [editModalUser, setEditModalUser] = useState<UserItem | null>(null);
    const [editFirstName, setEditFirstName] = useState("");
    const [editLastName, setEditLastName] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [editAddress, setEditAddress] = useState("");
    const [editZipCode, setEditZipCode] = useState("");
    const [editCityId, setEditCityId] = useState("");
    const [editPromoCodeAllowed, setEditPromoCodeAllowed] = useState(false);
    // «Выдаём билеты вручную» — отдельный тип распространителя в статистике.
    const [editManualDistributor, setEditManualDistributor] = useState(false);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [cities, setCities] = useState<CityItem[]>([]);
    const [citiesLoaded, setCitiesLoaded] = useState(false);

    // Модалка создания пользователя
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [createFirstName, setCreateFirstName] = useState("");
    const [createLastName, setCreateLastName] = useState("");
    const [createEmail, setCreateEmail] = useState("");
    const [createPassword, setCreatePassword] = useState("");
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Экспорт распространителей
    const [isExportingAgents, setIsExportingAgents] = useState(false);

    // Модалка "Отправить сообщение" распространителю
    const [messageModalUser, setMessageModalUser] = useState<UserItem | null>(null);
    const [messageSubject, setMessageSubject] = useState("");
    const [messageBody, setMessageBody] = useState("");
    const [messageAttachment, setMessageAttachment] = useState<File | null>(null);
    const [messageSubmitting, setMessageSubmitting] = useState(false);
    const [messageError, setMessageError] = useState<string | null>(null);

    const totalPages = useMemo(() => {
        if (totalSize === 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(totalSize / PAGE_SIZE));
    }, [totalSize]);

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);
        setActionError(null);
        setSuccessMessage(null);

        try {
            const response = await getUsersRequest({
                page,
                page_size: PAGE_SIZE,
                sort_by: "first_name",
                role: "customer",
                is_agent: isAgentFilter ?? undefined,
                club_member: clubMemberFilter ?? undefined,
                is_active: isActiveFilter ?? undefined,
                first_name: firstNameFilter.trim() || undefined,
                email: emailFilter.trim() || undefined,
            });

            setItems(response.items);
            setTotalSize(response.total_size);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось загрузить пользователей";
            setPageError(message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, isAgentFilter, clubMemberFilter, isActiveFilter, firstNameFilter, emailFilter]);

    const handleApplyFilters = () => {
        setPage(1);
        void loadData();
    };

    const handleResetFilters = () => {
        setIsAgentFilter(null);
        setClubMemberFilter(null);
        setIsActiveFilter(null);
        setFirstNameFilter("");
        setEmailFilter("");
        setPage(1);
        void loadData();
    };

    const handleToggleAgent = async (user: UserItem) => {
        setIsActionSubmitting(true);
        setActionError(null);
        setSuccessMessage(null);

        const currentAgent = user.customer_profile?.is_agent ?? false;

        const payload = currentAgent
            ? {
                is_agent: false,
                personal_discount_percent: 0,
            }
            : {
                is_agent: true,
                personal_discount_percent:
                    user.customer_profile?.personal_discount_percent ?? 0,
            };

        try {
            const updated = await updateUserRequest(user.id, payload);

            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );

            setSuccessMessage(
                !currentAgent
                    ? "Пользователь добавлен в агенты."
                    : "Пользователь удалён из агентов, скидка сброшена до 0%.",
            );
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось обновить пользователя";
            setActionError(message);
        } finally {
            setIsActionSubmitting(false);
        }
    };

    const handleChangeDiscount = async (user: UserItem, nextValue: string) => {
        const normalized = nextValue.trim();

        if (normalized === "") {
            // локально просто очищаем поле, а PATCH отправим по кнопке
            setItems((prev) =>
                prev.map((item) =>
                    item.id === user.id && item.customer_profile
                        ? {
                            ...item,
                            customer_profile: {
                                ...item.customer_profile,
                                personal_discount_percent: null,
                            },
                        }
                        : item,
                ),
            );
            return;
        }

        const numberValue = Number(normalized);

        if (Number.isNaN(numberValue)) {
            return;
        }

        if (numberValue < 0 || numberValue > 100) {
            return;
        }

        setIsActionSubmitting(true);
        setActionError(null);
        setSuccessMessage(null);

        try {
            const updated = await updateUserRequest(user.id, {
                personal_discount_percent: numberValue,
                is_agent: true,
            });

            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );

            setSuccessMessage("Персональная скидка обновлена.");
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось обновить скидку";
            setActionError(message);
        } finally {
            setIsActionSubmitting(false);
        }
    };

    const handleToggleActive = async (user: UserItem) => {
        setIsActionSubmitting(true);
        setActionError(null);
        setSuccessMessage(null);

        const nextActive = !user.is_active;

        try {
            const updated = await updateUserRequest(user.id, {
                is_active: nextActive,
            });

            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );

            setSuccessMessage(
                nextActive ? "Пользователь разблокирован." : "Пользователь заблокирован.",
            );
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось обновить статус";
            setActionError(message);
        } finally {
            setIsActionSubmitting(false);
        }
    };

    const handleCreateUser = async () => {
        setCreateError(null);

        const firstName = createFirstName.trim();
        const lastName = createLastName.trim();
        const email = createEmail.trim();
        const password = createPassword;

        if (!firstName || !lastName) {
            setCreateError("Укажите имя и фамилию.");
            return;
        }
        if (!email) {
            setCreateError("Укажите email.");
            return;
        }
        if (password.length < 8) {
            setCreateError("Пароль должен быть не короче 8 символов.");
            return;
        }

        // Кириллица в имени/фамилии — предупреждаем (backend, возможно, тоже валидирует).
        const latinRe = /^[A-Za-zÄÖÜäöüß\s'’\-]+$/;
        if (!latinRe.test(firstName) || !latinRe.test(lastName)) {
            setCreateError(
                "Имя и фамилия должны быть только латинскими буквами.",
            );
            return;
        }

        setCreateSubmitting(true);
        try {
            await registerCustomerRequest({
                first_name: firstName,
                last_name: lastName,
                email,
                password,
                send_welcome_email: false,
            });
            setIsCreateOpen(false);
            setSuccessMessage(`Пользователь ${email} создан.`);
            void loadData();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось создать пользователя.";
            setCreateError(message);
        } finally {
            setCreateSubmitting(false);
        }
    };

    const handleSaveDiscount = async () => {
        if (!discountModalUser) {
            return;
        }

        const normalized = discountModalValue.trim();

        const numberValue = Number(normalized);

        if (Number.isNaN(numberValue)) {
            setActionError("Введите число от 0 до 100.");
            return;
        }

        if (numberValue < 0 || numberValue > 100) {
            setActionError("Скидка должна быть от 0 до 100%.");
            return;
        }

        setIsActionSubmitting(true);
        setActionError(null);
        setSuccessMessage(null);

        try {
            const updated = await updateUserRequest(discountModalUser.id, {
                personal_discount_percent: numberValue,
                is_agent: true,
            });

            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );

            setSuccessMessage("Персональная скидка обновлена.");
            setDiscountModalUser(null);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось обновить скидку";
            setActionError(message);
        } finally {
            setIsActionSubmitting(false);
        }
    };

    const ensureCitiesLoaded = async () => {
        if (citiesLoaded) {
            return;
        }
        try {
            const response = await getCitiesRequest();
            setCities(response);
        } catch (error) {
            // Список городов не критичен для редактирования, просто оставим поле пустым.
        } finally {
            setCitiesLoaded(true);
        }
    };

    const handleOpenEdit = (user: UserItem) => {
        const profile = user.customer_profile;

        setEditModalUser(user);
        setEditFirstName(user.first_name ?? "");
        setEditLastName(user.last_name ?? "");
        setEditPhone(profile?.phone ?? "");
        setEditAddress(profile?.address ?? "");
        setEditZipCode(profile?.zip_code ?? "");
        setEditCityId(profile?.city_id ?? "");
        setEditPromoCodeAllowed(profile?.promo_code_allowed ?? false);
        setEditManualDistributor(profile?.is_manual_distributor ?? false);
        setEditError(null);
        void ensureCitiesLoaded();
    };

    const handleOpenMessageModal = (user: UserItem) => {
        setMessageModalUser(user);
        setMessageSubject(DEFAULT_DISTRIBUTOR_MESSAGE_SUBJECT);
        setMessageBody(buildDefaultDistributorMessageBody(user.email));
        setMessageAttachment(null);
        setMessageError(null);
    };

    const handleSendMessage = async () => {
        if (!messageModalUser) {
            return;
        }

        const subject = messageSubject.trim();
        const body = messageBody.trim();

        if (!subject || !body) {
            setMessageError("Укажите тему и текст письма.");
            return;
        }

        setMessageSubmitting(true);
        setMessageError(null);

        try {
            await sendDistributorMessageRequest(messageModalUser.id, {
                subject,
                body,
                attachment: messageAttachment,
            });

            setSuccessMessage(`Письмо отправлено на ${messageModalUser.email}.`);
            setMessageModalUser(null);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось отправить письмо.";
            setMessageError(message);
        } finally {
            setMessageSubmitting(false);
        }
    };

    const handleDownloadAgents = async () => {
        setIsExportingAgents(true);
        setActionError(null);

        try {
            const { blob, filename } = await downloadAgentsExportRequest();
            triggerBrowserDownload(blob, filename);
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Не удалось скачать список распространителей.";
            setActionError(message);
        } finally {
            setIsExportingAgents(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editModalUser) {
            return;
        }

        const firstName = editFirstName.trim();
        const lastName = editLastName.trim();

        if (!firstName || !lastName) {
            setEditError("Укажите имя и фамилию.");
            return;
        }

        setEditSubmitting(true);
        setEditError(null);

        try {
            const isAgentUser = editModalUser.customer_profile?.is_agent ?? false;

            const updated = await updateUserRequest(editModalUser.id, {
                first_name: firstName,
                last_name: lastName,
                phone: editPhone.trim(),
                address: editAddress.trim(),
                zip_code: editZipCode.trim(),
                city_id: editCityId || undefined,
                // разрешение промокода редактируется только для распространителей
                ...(isAgentUser
                    ? {
                          promo_code_allowed: editPromoCodeAllowed,
                          is_manual_distributor: editManualDistributor,
                      }
                    : {}),
            });

            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? updated : item)),
            );

            setSuccessMessage("Данные пользователя обновлены.");
            setEditModalUser(null);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : "Не удалось обновить данные пользователя.";
            setEditError(message);
        } finally {
            setEditSubmitting(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Пользователи"
                description="Список клиентов с фильтрацией, признаком агента, клубного участника и управлением персональной скидкой."
            />

                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список пользователей</h3>
                            <p className="card-text">
                                Фильтруйте клиентов по признакам агента, клубного участника, активности
                                и контактным данным.
                            </p>
                        </div>
                        <div className="editor-actions">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setCreateFirstName("");
                                    setCreateLastName("");
                                    setCreateEmail("");
                                    setCreatePassword("");
                                    setCreateError(null);
                                    setIsCreateOpen(true);
                                }}
                            >
                                Создать пользователя
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleDownloadAgents}
                                disabled={isExportingAgents}
                            >
                                {isExportingAgents
                                    ? "Скачиваем..."
                                    : "Скачать список распространителей"}
                            </Button>
                        </div>

                        <div className="section-toolbar-controls">
                            <Input
                                type="text"
                                placeholder="Поиск по имени..."
                                value={firstNameFilter}
                                onChange={(event) => setFirstNameFilter(event.target.value)}
                            />
                            <Input
                                type="email"
                                placeholder="Поиск по e-mail..."
                                value={emailFilter}
                                onChange={(event) => setEmailFilter(event.target.value)}
                                style={{ marginTop: 8 }}
                            />
                        </div>
                    </div>

                    <div className="users-filters-row">
                        <Select
                            value={
                                isAgentFilter === null ? "" : isAgentFilter ? "true" : "false"
                            }
                            onChange={(event) => {
                                const value = event.target.value;
                                if (value === "") {
                                    setIsAgentFilter(null);
                                } else {
                                    setIsAgentFilter(value === "true");
                                }
                            }}
                            options={[
                                { value: "true", label: "Только агенты" },
                                { value: "false", label: "Без агента" },
                            ]}
                            placeholder="Фильтр агент"
                        />

                        {/*<Select*/}
                        {/*    value={*/}
                        {/*        clubMemberFilter === null*/}
                        {/*            ? ""*/}
                        {/*            : clubMemberFilter*/}
                        {/*                ? "true"*/}
                        {/*                : "false"*/}
                        {/*    }*/}
                        {/*    onChange={(event) => {*/}
                        {/*        const value = event.target.value;*/}
                        {/*        if (value === "") {*/}
                        {/*            setClubMemberFilter(null);*/}
                        {/*        } else {*/}
                        {/*            setClubMemberFilter(value === "true");*/}
                        {/*        }*/}
                        {/*    }}*/}
                        {/*    options={[*/}
                        {/*        { value: "true", label: "Только члены клуба" },*/}
                        {/*        { value: "false", label: "Не члены клуба" },*/}
                        {/*    ]}*/}
                        {/*    placeholder="Фильтр клуб"*/}
                        {/*/>*/}

                        <Select
                            value={
                                isActiveFilter === null
                                    ? ""
                                    : isActiveFilter
                                        ? "true"
                                        : "false"
                            }
                            onChange={(event) => {
                                const value = event.target.value;
                                if (value === "") {
                                    setIsActiveFilter(null);
                                } else {
                                    setIsActiveFilter(value === "true");
                                }
                            }}
                            options={[
                                { value: "true", label: "Только активные" },
                                { value: "false", label: "Заблокированные" },
                            ]}
                            placeholder="Фильтр активность"
                        />

                        <div className="editor-actions">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleResetFilters}
                            >
                                Сбросить
                            </Button>
                        </div>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Пользователи не найдены</h3>
                            <p className="empty-block-text">
                                Измените параметры фильтрации или попробуйте позже.
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <thead>
                                <tr>
                                    <th>Имя</th>
                                    <th>Город</th>
                                    <th>Контакты</th>
                                    <th>Распространитель</th>
                                    <th>Персональная скидка</th>
                                    <th>Регистрация</th>
                                    <th>Статус</th>
                                    <th>Действия</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((user) => {
                                    const profile = user.customer_profile;
                                    const city = profile?.city;
                                    const isAgent = profile?.is_agent ?? false;
                                    const discount =
                                        profile?.personal_discount_percent ?? null;
                                    const registeredAt = profile?.registered_at ?? user.created_at;
                                    const status =
                                        profile?.status ?? (user.is_active ? "active" : "blocked");

                                    return (
                                        <tr key={user.id}>
                                            <td>
                                                <div className="table-primary-cell">
                                                    <span className="table-primary-text">
                                                        {user.first_name} {user.last_name}
                                                    </span>
                                                    <span className="table-secondary-text">
                                                        {user.email}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                {city
                                                    ? `${city.name_ru}, ${city.country.name_ru}`
                                                    : "—"}
                                            </td>
                                            <td>
                                                <div className="table-secondary-text">
                                                    {profile?.phone ?? "—"}
                                                    <br />
                                                    {profile?.address ?? ""}
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    className={`status-badge ${
                                                        isAgent
                                                            ? "status-badge-active"
                                                            : "status-badge-inactive"
                                                    }`}
                                                >
                                                    {isAgent ? "Распространитель" : "Не распространитель"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="editor-actions">
                                                    {profile?.personal_discount_percent}
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={() => {
                                                            if (!isAgent) {
                                                                return;
                                                            }

                                                            setDiscountModalUser(user);
                                                            setDiscountModalValue(
                                                                discount !== null ? String(discount) : "0",
                                                            );
                                                        }}
                                                        disabled={!isAgent || isActionSubmitting}
                                                    >
                                                        %
                                                    </Button>
                                                </div>
                                            </td>
                                            <td>{formatDateTime(registeredAt)}</td>
                                            <td>
                                                <span
                                                    className={`status-badge ${
                                                        user.is_active
                                                            ? "status-badge-active"
                                                            : "status-badge-inactive"
                                                    }`}
                                                >
                                                    {status === "blocked"
                                                        ? "Заблокирован"
                                                        : "Активен"}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="editor-actions">
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={() => handleOpenEdit(user)}
                                                        disabled={isActionSubmitting}
                                                        title="Редактировать"
                                                        aria-label="Редактировать"
                                                    >
                                                        <Edit2 size={16} />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={() => handleToggleAgent(user)}
                                                        disabled={isActionSubmitting}
                                                    >
                                                        {isAgent
                                                            ? "Убрать из распрост."
                                                            : "Сделать распрост."}
                                                    </Button>
                                                    {isAgent ? (
                                                        <Button
                                                            type="button"
                                                            variant="secondary"
                                                            onClick={() => handleOpenMessageModal(user)}
                                                            disabled={isActionSubmitting}
                                                            title="Отправить сообщение"
                                                        >
                                                            <Mail size={16} style={{ marginRight: 6 }} />
                                                            Отправить сообщение
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => handleToggleActive(user)}
                                                        disabled={isActionSubmitting}
                                                    >
                                                        {user.is_active
                                                            ? "Заблокировать"
                                                            : "Разблокировать"}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalSize > PAGE_SIZE ? (
                        <div className="pagination-row">
                            <span className="pagination-meta">
                                Страница {page} из {totalPages} • Всего {totalSize} пользователей
                            </span>

                            <div className="editor-actions">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                    disabled={page <= 1}
                                >
                                    Назад
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() =>
                                        setPage((prev) =>
                                            Math.min(totalPages, prev + 1),
                                        )
                                    }
                                    disabled={page >= totalPages}
                                >
                                    Вперёд
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </Card>

            {isCreateOpen && (
                <div
                    className="modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Создание пользователя"
                >
                    <div className="modal">
                        <h3 className="card-title">Новый пользователь</h3>
                        <p className="card-text">
                            Клиент будет создан без отправки welcome-письма.
                            После создания передайте ему email и пароль лично.
                        </p>

                        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
                            <Input
                                type="text"
                                placeholder="Имя (латиница)"
                                value={createFirstName}
                                onChange={(e) => setCreateFirstName(e.target.value)}
                                autoFocus
                            />
                            <Input
                                type="text"
                                placeholder="Фамилия (латиница)"
                                value={createLastName}
                                onChange={(e) => setCreateLastName(e.target.value)}
                            />
                            <Input
                                type="email"
                                placeholder="Email"
                                value={createEmail}
                                onChange={(e) => setCreateEmail(e.target.value)}
                            />
                            <Input
                                type="password"
                                placeholder="Пароль (от 8 символов)"
                                value={createPassword}
                                onChange={(e) => setCreatePassword(e.target.value)}
                            />
                        </div>

                        {createError && (
                            <p style={{ color: "#ef4444", marginTop: 12 }}>{createError}</p>
                        )}

                        <div className="editor-actions" style={{ marginTop: 24 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleCreateUser}
                                disabled={createSubmitting}
                            >
                                {createSubmitting ? "Создаём..." : "Создать"}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setIsCreateOpen(false)}
                                disabled={createSubmitting}
                            >
                                Отмена
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {editModalUser && (
                <div
                    className="modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Редактирование пользователя"
                >
                    <div className="modal">
                        <h3 className="card-title">
                            Редактирование пользователя
                        </h3>
                        <p className="card-text">
                            {editModalUser.email} — email и пароль здесь изменить нельзя.
                        </p>

                        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
                            <Input
                                type="text"
                                placeholder="Имя"
                                value={editFirstName}
                                onChange={(e) => setEditFirstName(e.target.value)}
                                autoFocus
                            />
                            <Input
                                type="text"
                                placeholder="Фамилия"
                                value={editLastName}
                                onChange={(e) => setEditLastName(e.target.value)}
                            />
                            <Input
                                type="tel"
                                placeholder="Телефон"
                                value={editPhone}
                                onChange={(e) => setEditPhone(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Адрес"
                                value={editAddress}
                                onChange={(e) => setEditAddress(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Почтовый индекс"
                                value={editZipCode}
                                onChange={(e) => setEditZipCode(e.target.value)}
                            />
                            <Select
                                value={editCityId}
                                onChange={(e) => setEditCityId(e.target.value)}
                                options={cities.map((c) => ({
                                    value: c.id,
                                    label: c.name_ru,
                                }))}
                                placeholder="Город не выбран"
                            />

                            {editModalUser.customer_profile?.is_agent ? (
                                <Switch
                                    checked={editPromoCodeAllowed}
                                    onChange={setEditPromoCodeAllowed}
                                    label="Разрешено использовать промокод"
                                    disabled={editSubmitting}
                                />
                            ) : null}

                            {editModalUser.customer_profile?.is_agent ? (
                                <Switch
                                    checked={editManualDistributor}
                                    onChange={setEditManualDistributor}
                                    label="Выдаём билеты вручную (отдельный тип в статистике)"
                                    disabled={editSubmitting}
                                />
                            ) : null}
                        </div>

                        {editError && (
                            <p style={{ color: "#ef4444", marginTop: 12 }}>{editError}</p>
                        )}

                        <div className="editor-actions" style={{ marginTop: 24 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleSaveEdit}
                                disabled={editSubmitting}
                            >
                                {editSubmitting ? "Сохраняем..." : "Сохранить"}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setEditModalUser(null)}
                                disabled={editSubmitting}
                            >
                                Отмена
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {messageModalUser && (
                <div
                    className="modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Отправка сообщения распространителю"
                >
                    <div className="modal">
                        <h3 className="card-title">
                            Отправить сообщение — {messageModalUser.first_name}{" "}
                            {messageModalUser.last_name}
                        </h3>
                        <p className="card-text">
                            Письмо будет отправлено на {messageModalUser.email}. Ответы распространителя придут на office@showmust.de.
                        </p>

                        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
                            <Input
                                type="text"
                                placeholder="Тема письма"
                                value={messageSubject}
                                onChange={(e) => setMessageSubject(e.target.value)}
                                autoFocus
                            />
                            <Textarea
                                placeholder="Текст письма"
                                value={messageBody}
                                onChange={(e) => setMessageBody(e.target.value)}
                                rows={12}
                            />

                            <div>
                                <span className="ui-label">Вложение</span>
                                <div style={{ marginTop: 6 }}>
                                    <span className="table-secondary-text">
                                        {messageAttachment
                                            ? messageAttachment.name
                                            : DEFAULT_DISTRIBUTOR_ATTACHMENT_HINT}
                                    </span>
                                </div>
                                <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                                    <input
                                        type="file"
                                        id="distributor-message-attachment"
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                            setMessageAttachment(file);
                                        }}
                                    />
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() =>
                                            document.getElementById("distributor-message-attachment")?.click()
                                        }
                                        disabled={messageSubmitting}
                                    >
                                        Заменить файл
                                    </Button>
                                    {messageAttachment ? (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => setMessageAttachment(null)}
                                            disabled={messageSubmitting}
                                        >
                                            Сбросить до файла по умолчанию
                                        </Button>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        {messageError && (
                            <p style={{ color: "#ef4444", marginTop: 12 }}>{messageError}</p>
                        )}

                        <div className="editor-actions" style={{ marginTop: 24 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleSendMessage}
                                disabled={messageSubmitting}
                            >
                                {messageSubmitting ? "Отправляем..." : "Отправить"}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setMessageModalUser(null)}
                                disabled={messageSubmitting}
                            >
                                Отмена
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {discountModalUser && (
                <div
                    className="modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Изменение персональной скидки"
                >
                    <div className="modal">
                        <h3 className="card-title">
                            Персональная скидка — {discountModalUser.first_name}{" "}
                            {discountModalUser.last_name}
                        </h3>
                        <p className="card-text">
                            Укажите размер скидки в процентах. При снятии признака распространителя скидка
                            автоматически сбрасывается до 0%.
                        </p>

                        <div style={{ marginTop: 16 }}>
                            <Input
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={discountModalValue}
                                onChange={(event) => setDiscountModalValue(event.target.value)}
                                style={{ width: 120 }}
                            />
                            <span style={{ marginLeft: 8 }}>%</span>
                        </div>

                        <div className="editor-actions" style={{ marginTop: 24 }}>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleSaveDiscount}
                                disabled={isActionSubmitting}
                            >
                                Сохранить
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setDiscountModalUser(null)}
                                disabled={isActionSubmitting}
                            >
                                Отмена
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
